import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { expenseAPI } from './expenseAPI';
import {
  Expense,
  CreateExpenseData,
  UpdateExpenseData,
  ExpenseQueryParams,
  ExpenseSummary,
} from '../../types/expense';
import { PaginationMeta } from '../../types/pagination';

interface ExpenseState {
  expenses: Expense[];
  currentExpense: Expense | null;
  summary: ExpenseSummary | null;
  pagination: PaginationMeta | null;
  loading: boolean;
  summaryLoading: boolean;
  error: string | null;
  filters: ExpenseQueryParams;
}

const initialState: ExpenseState = {
  expenses: [],
  currentExpense: null,
  summary: null,
  pagination: null,
  loading: false,
  summaryLoading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    search: '',
    category: undefined,
    paymentMode: undefined,
    storeId: undefined,
    startDate: undefined,
    endDate: undefined,
    sortBy: 'expenseDate',
    sortOrder: 'desc',
  },
};

// Async thunks
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (params: ExpenseQueryParams = {}) => {
    const response = await expenseAPI.getExpenses(params);
    return response;
  }
);

export const fetchExpense = createAsyncThunk(
  'expenses/fetchExpense',
  async (uuid: string) => {
    const response = await expenseAPI.getExpense(uuid);
    return response;
  }
);

export const createExpense = createAsyncThunk(
  'expenses/createExpense',
  async (data: CreateExpenseData, { rejectWithValue }) => {
    try {
      const response = await expenseAPI.createExpense(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create expense');
    }
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async ({ uuid, data }: { uuid: string; data: UpdateExpenseData }, { rejectWithValue }) => {
    try {
      const response = await expenseAPI.updateExpense(uuid, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update expense');
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/deleteExpense',
  async (uuid: string, { rejectWithValue }) => {
    try {
      await expenseAPI.deleteExpense(uuid);
      return uuid;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete expense');
    }
  }
);

export const restoreExpense = createAsyncThunk(
  'expenses/restoreExpense',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await expenseAPI.restoreExpense(uuid);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to restore expense');
    }
  }
);

export const fetchExpenseSummary = createAsyncThunk(
  'expenses/fetchSummary',
  async (params: { startDate?: string; endDate?: string; storeId?: string } = {}) => {
    const response = await expenseAPI.getExpenseSummary(params);
    return response;
  }
);

const expenseSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ExpenseQueryParams>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentExpense: (state) => {
      state.currentExpense = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch expenses
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch expenses';
      })

      // Fetch single expense
      .addCase(fetchExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.currentExpense = action.payload.data;
      })
      .addCase(fetchExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch expense';
      })

      // Create expense
      .addCase(createExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update expense
      .addCase(updateExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.loading = false;
        const updatedExpense = action.payload.data;
        if (updatedExpense) {
          const index = state.expenses.findIndex((e) => e.uuid === updatedExpense.uuid);
          if (index !== -1) {
            state.expenses[index] = updatedExpense;
          }
          if (state.currentExpense?.uuid === updatedExpense.uuid) {
            state.currentExpense = updatedExpense;
          }
        }
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete expense
      .addCase(deleteExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.filter((e) => e.uuid !== action.payload);
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Restore expense
      .addCase(restoreExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreExpense.fulfilled, (state, action) => {
        state.loading = false;
        const restoredExpense = action.payload.data;
        if (restoredExpense) {
          state.expenses.push(restoredExpense);
        }
      })
      .addCase(restoreExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch summary
      .addCase(fetchExpenseSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchExpenseSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload.data;
      })
      .addCase(fetchExpenseSummary.rejected, (state) => {
        state.summaryLoading = false;
      });
  },
});

export const { setFilters, clearCurrentExpense, clearError, resetFilters } = expenseSlice.actions;

// Selectors
export const selectExpenses = (state: { expenses: ExpenseState }) => state.expenses.expenses;
export const selectCurrentExpense = (state: { expenses: ExpenseState }) => state.expenses.currentExpense;
export const selectExpenseSummary = (state: { expenses: ExpenseState }) => state.expenses.summary;
export const selectExpensePagination = (state: { expenses: ExpenseState }) => state.expenses.pagination;
export const selectExpenseLoading = (state: { expenses: ExpenseState }) => state.expenses.loading;
export const selectExpenseSummaryLoading = (state: { expenses: ExpenseState }) => state.expenses.summaryLoading;
export const selectExpenseError = (state: { expenses: ExpenseState }) => state.expenses.error;
export const selectExpenseFilters = (state: { expenses: ExpenseState }) => state.expenses.filters;

export default expenseSlice.reducer;
